import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Spinner,
} from "react-bootstrap";
import CommonLayout from "../../../layouts/CommonLayout";
import { useNavigate } from "react-router-dom";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { toast } from "react-toastify";
import { extractObjectId } from "../../../utils/utils";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import {
  FaMusic,
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaImage,
  FaGlobe,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
  FaTags,
} from "react-icons/fa";
import "./GenresPage.css";

function GenresPage() {
  const [genres, setGenres] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [genreToDelete, setGenreToDelete] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGenre, setEditingGenre] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const [formData, setFormData] = useState({
    genreName: '',
    description: '',
    image: '',
    keywords: '',
    origin: '',
    era: '',
  });

  const navigate = useNavigate();

  const fetchGenres = async () => {
    try {
      setIsLoading(true);
      const response = await allAPiServicesCall.getGenres(
        { page: currentPage, limit, search: searchQuery },
        getAuthConfig(),
        navigate
      );
      if (response?.data && Array.isArray(response.data)) {
        setGenres(response.data);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalCount(response.pagination?.totalCount || response.data.length);
      }
    } catch (error) {
      console.error("Error fetching genres:", error);
      toast.error("Failed to fetch genres");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGenres();
  }, [currentPage, searchQuery]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddGenre = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
      };
      const response = await allAPiServicesCall.createGenre(payload, getAuthConfig(), navigate);
      if (response?.success) {
        setShowAddModal(false);
        resetForm();
        fetchGenres();
      }
    } catch (error) {
      console.error("Error creating genre:", error);
      toast.error(error?.response?.data?.message || "Failed to create genre");
    }
  };

  const handleEditGenre = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
      };
      const response = await allAPiServicesCall.updateGenre(
        extractObjectId(editingGenre._id),
        payload,
        getAuthConfig(),
        navigate
      );
      if (response?.success) {
        setShowEditModal(false);
        setEditingGenre(null);
        resetForm();
        fetchGenres();
      }
    } catch (error) {
      console.error("Error updating genre:", error);
      toast.error(error?.response?.data?.message || "Failed to update genre");
    }
  };

  const confirmDelete = (genre) => {
    setGenreToDelete(genre);
    setShowDeleteModal(true);
  };

  const handleDeleteGenre = async () => {
    if (!genreToDelete) return;
    try {
      const response = await allAPiServicesCall.deleteGenre(
        extractObjectId(genreToDelete._id),
        getAuthConfig(),
        navigate
      );
      if (response?.success) {
        setShowDeleteModal(false);
        setGenreToDelete(null);
        fetchGenres();
      }
    } catch (error) {
      console.error("Error deleting genre:", error);
      toast.error("Failed to delete genre");
    }
  };

  const openEditModal = (genre) => {
    setEditingGenre(genre);
    setFormData({
      genreName: genre.genreName || '',
      description: genre.description || '',
      image: genre.image || '',
      keywords: Array.isArray(genre.keywords) ? genre.keywords.join(', ') : '',
      origin: genre.origin || '',
      era: genre.era || '',
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      genreName: '',
      description: '',
      image: '',
      keywords: '',
      origin: '',
      era: '',
    });
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <CommonLayout>
      <div className="genres-page">
        {/* Header Section */}
        <div className="genres-header">
          <div className="genres-header-content">
            <div className="genres-header-icon">
              <FaMusic />
            </div>
            <div className="genres-header-text">
              <h1 className="genres-title">Genres Management</h1>
              <p className="genres-subtitle">
                Organize and manage music genres for your platform
              </p>
            </div>
          </div>
          <button className="genres-add-btn" onClick={() => setShowAddModal(true)}>
            <FaPlus /> Add New Genre
          </button>
        </div>

        {/* Search & Stats Controls */}
        <div className="genres-controls">
          <div className="genres-search-wrapper">
            <FaSearch className="genres-search-icon" />
            <input
              type="text"
              className="genres-search-input"
              placeholder="Search genres by name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="genres-stat-card">
            <FaTags className="genres-stat-icon" />
            <div className="genres-stat-info">
              <span className="genres-stat-value">{totalCount}</span>
              <span className="genres-stat-label">Total Genres</span>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="genres-loading">
            <div className="genres-loading-spinner">
              <Spinner animation="border" />
            </div>
            <p className="genres-loading-text">Loading genres...</p>
          </div>
        ) : genres.length === 0 ? (
          <div className="genres-empty">
            <div className="genres-empty-icon">
              <FaMusic />
            </div>
            <h3 className="genres-empty-title">No Genres Found</h3>
            <p className="genres-empty-text">
              {searchQuery
                ? "No genres match your search. Try a different keyword."
                : "Create your first genre to get started organizing your music library."}
            </p>
          </div>
        ) : (
          <div className="genres-table-container">
            <table className="genres-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Keywords</th>
                  <th>Origin</th>
                  <th>Era</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {genres.map((genre) => (
                  <tr key={genre._id}>
                    <td className="genre-image-cell">
                      <div className="genre-image-wrapper">
                        {genre.image ? (
                          <img
                            src={genre.image}
                            alt={genre.genreName}
                            className="genre-image"
                          />
                        ) : (
                          <div className="genre-image-placeholder">N/A</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="genre-name">
                        {genre.genreName}
                      </div>
                    </td>
                    <td>
                      {genre.description ? (
                        <div className="genre-description">
                          {genre.description.length > 80
                            ? `${genre.description.substring(0, 80)}...`
                            : genre.description}
                        </div>
                      ) : (
                        <span className="genre-empty-cell">-</span>
                      )}
                    </td>
                    <td>
                      {Array.isArray(genre.keywords) && genre.keywords.length > 0 ? (
                        <div className="genre-keywords">
                          {genre.keywords.slice(0, 3).map((keyword, idx) => (
                            <span key={idx} className="genre-keyword-tag">
                              {keyword}
                            </span>
                          ))}
                          {genre.keywords.length > 3 && (
                            <span className="genre-keyword-more">
                              +{genre.keywords.length - 3}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="genre-empty-cell">-</span>
                      )}
                    </td>
                    <td>
                      {genre.origin ? (
                        <span className="genre-info-badge">
                          <FaGlobe />
                          {genre.origin}
                        </span>
                      ) : (
                        <span className="genre-empty-cell">-</span>
                      )}
                    </td>
                    <td>
                      {genre.era ? (
                        <span className="genre-info-badge">
                          <FaClock />
                          {genre.era}
                        </span>
                      ) : (
                        <span className="genre-empty-cell">-</span>
                      )}
                    </td>
                    <td>
                      <div className="genre-actions">
                        <button
                          className="genre-action-btn genre-action-btn-edit"
                          onClick={() => openEditModal(genre)}
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          className="genre-action-btn genre-action-btn-delete"
                          onClick={() => confirmDelete(genre)}
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="genres-pagination">
                <button
                  className="genres-pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Previous
                </button>
                <div className="genres-pagination-info">
                  <span className="genres-pagination-current">{currentPage}</span>
                  <span className="genres-pagination-separator">/</span>
                  <span className="genres-pagination-total">{totalPages}</span>
                </div>
                <button
                  className="genres-pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Genre Modal */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        size="lg"
        className="genres-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaPlus /> Add New Genre
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddGenre}>
          <Modal.Body>
            <div className="genres-form-grid">
              <Form.Group className="mb-3">
                <Form.Label>Genre Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="genreName"
                  value={formData.genreName}
                  onChange={handleFormChange}
                  required
                  placeholder="e.g., Hip Hop, Rock, Jazz"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Image URL</Form.Label>
                <Form.Control
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleFormChange}
                  placeholder="https://example.com/image.jpg"
                />
              </Form.Group>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                placeholder="Brief description of this genre"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Keywords (comma-separated)</Form.Label>
              <Form.Control
                type="text"
                name="keywords"
                value={formData.keywords}
                onChange={handleFormChange}
                placeholder="e.g., urban, beats, rhythm"
              />
              <Form.Text className="text-muted">
                Enter keywords separated by commas for better searchability
              </Form.Text>
            </Form.Group>
            <div className="genres-form-grid">
              <Form.Group className="mb-3">
                <Form.Label>Origin</Form.Label>
                <Form.Control
                  type="text"
                  name="origin"
                  value={formData.origin}
                  onChange={handleFormChange}
                  placeholder="e.g., United States, Jamaica"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Era</Form.Label>
                <Form.Control
                  type="text"
                  name="era"
                  value={formData.era}
                  onChange={handleFormChange}
                  placeholder="e.g., 1970s, 1990s-present"
                />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="genres-modal-btn-cancel"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </button>
            <button type="submit" className="genres-modal-btn-primary">
              <FaPlus /> Create Genre
            </button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Genre Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        className="genres-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEdit /> Edit Genre
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditGenre}>
          <Modal.Body>
            <div className="genres-form-grid">
              <Form.Group className="mb-3">
                <Form.Label>Genre Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="genreName"
                  value={formData.genreName}
                  onChange={handleFormChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Image URL</Form.Label>
                <Form.Control
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleFormChange}
                  placeholder="https://example.com/image.jpg"
                />
              </Form.Group>
            </div>
            {formData.image && (
              <div className="mb-3">
                <Form.Label>Image Preview</Form.Label>
                <div className="genre-image-preview">
                  <img
                    src={formData.image}
                    alt="Genre preview"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Keywords (comma-separated)</Form.Label>
              <Form.Control
                type="text"
                name="keywords"
                value={formData.keywords}
                onChange={handleFormChange}
              />
            </Form.Group>
            <div className="genres-form-grid">
              <Form.Group className="mb-3">
                <Form.Label>Origin</Form.Label>
                <Form.Control
                  type="text"
                  name="origin"
                  value={formData.origin}
                  onChange={handleFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Era</Form.Label>
                <Form.Control
                  type="text"
                  name="era"
                  value={formData.era}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="genres-modal-btn-cancel"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </button>
            <button type="submit" className="genres-modal-btn-primary">
              <FaEdit /> Update Genre
            </button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteGenre}
        title="Delete Genre"
        message={`Are you sure you want to delete the genre "${genreToDelete?.genreName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </CommonLayout>
  );
}

export default GenresPage;
